// SETTINGS: vertical tabs on the left, the form on the right. Every change
// applies at once; each tab can be reset to its defaults.

import { useEffect, useRef, useState } from 'preact/hooks';
import { ACTIONS, DEFAULT_BINDS, assign, cloneBinds, findConflicts, keyLabel, type Action } from '../../game/binds';
import { DEFAULT_HUD, DEFAULT_SETTINGS, M_YAW, type Settings } from '../../game/settings';
import { Button } from '../components/Button';
import { KeyBind } from '../components/KeyBind';
import { Levelshot } from '../components/Levelshot';
import { Select } from '../components/Select';
import { Slider } from '../components/Slider';
import { Tabs } from '../components/Tabs';
import { Toggle } from '../components/Toggle';
import { useApp } from '../context';
import { Hud, KEY_FORWARD, KEY_JUMP, KEY_RIGHT, helpText } from '../hud';
import { confirmDialog, settings, toast, updateHud, updateSettings } from '../state';

type SettingsTab = 'game' | 'mouse' | 'video' | 'sound' | 'hud' | 'controls' | 'accessibility';

const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'game', label: 'Game' },
  { id: 'mouse', label: 'Mouse' },
  { id: 'video', label: 'Video' },
  { id: 'sound', label: 'Sound' },
  { id: 'hud', label: 'HUD' },
  { id: 'controls', label: 'Controls' },
  { id: 'accessibility', label: 'Accessibility' },
];

/** Settings each tab resets. */
const TAB_KEYS: Record<SettingsTab, (keyof Settings)[]> = {
  game: ['physicsMsec', 'showTjl', 'drawGun', 'thirdPerson', 'thirdPersonRange', 'thirdPersonAngle', 'showHelp', 'autoHideHelp'],
  mouse: ['sensitivity', 'mouseDpi', 'invertMouse', 'rawInput'],
  video: ['fov', 'brightness', 'renderScale', 'lowGraphics', 'showFps'],
  sound: ['volume', 'effectsVolume', 'ambientVolume', 'otherPlayerSounds'],
  hud: ['hud'],
  controls: ['binds'],
  accessibility: ['uiScale', 'reduceMotion', 'largeHudText', 'highContrast'],
};

const FPS_OPTIONS = [
  { value: 8, label: '125 fps (8 ms) — standard' },
  { value: 4, label: '250 fps (4 ms)' },
  { value: 3, label: '333 fps (3 ms)' },
  { value: 13, label: '76 fps (13 ms)' },
  { value: 23, label: '43 fps (23 ms)' },
];

const pct = (v: number) => `${Math.round(v * 100)}%`;

function GameTab({ s }: { s: Settings }) {
  const app = useApp();
  return (
    <>
      <Select
        label="Physics frame rate"
        hint="Physics frame rate (pmove_msec). Changing it ends the current run."
        value={s.physicsMsec}
        options={FPS_OPTIONS}
        onChange={async (msec) => {
          if (app.runActive && !(await confirmDialog({ title: 'Change physics frame rate?', body: 'Your current run will be stopped.', confirm: 'Change', danger: true }))) return;
          app.setPhysicsMsec(msec);
        }}
      />
      <Toggle label="Strafe helper (CGaz)" hint="Shows the yaw ranges that gain speed under the crosshair." checked={s.hud.cgaz} onChange={(v) => updateHud({ cgaz: v })} />
      <Toggle label="Trickjump line" hint="Shows the route you recorded on this map (K or /tjl record, then /tjl stop)." checked={s.showTjl} onChange={(v) => updateSettings({ showTjl: v })} />
      <Toggle label="Knife in hand" hint="Draw the knife in first person, like cg_drawGun." checked={s.drawGun} onChange={(v) => updateSettings({ drawGun: v })} />
      <Toggle label="Third person view" hint="See your own player (and the one you spectate) from behind, like cg_thirdPerson." checked={s.thirdPerson} onChange={(v) => updateSettings({ thirdPerson: v })} />
      {s.thirdPerson && (
        <>
          <Slider label="Camera distance" hint="cg_thirdPersonRange (ET: 80)." value={s.thirdPersonRange} min={40} max={400} step={5} onChange={(v) => updateSettings({ thirdPersonRange: v })} />
          <Slider label="Camera angle" hint="Around the player: 0 behind, 90 from the left side, 180 in front." value={s.thirdPersonAngle} min={-180} max={180} step={5} unit="°" onChange={(v) => updateSettings({ thirdPersonAngle: v })} />
        </>
      )}
      <Toggle label="Controls help" checked={s.showHelp} onChange={(v) => updateSettings({ showHelp: v })} />
      <Toggle label="Auto-hide help" hint="Hide the controls help a few seconds after resuming." checked={s.autoHideHelp} onChange={(v) => updateSettings({ autoHideHelp: v })} />
    </>
  );
}

function MouseTab({ s }: { s: Settings }) {
  // counts for a full turn -> inches -> cm
  const cm360 = ((360 / (s.sensitivity * M_YAW)) / s.mouseDpi) * 2.54;
  return (
    <>
      <Slider label="Sensitivity" hint="ET sensitivity (m_yaw 0.022)." value={s.sensitivity} min={0.1} max={30} step={0.01} onChange={(v) => updateSettings({ sensitivity: v })} />
      <Slider label="Mouse DPI" hint="Only used for the cm/360 figure below." value={s.mouseDpi} min={100} max={12800} step={50} onChange={(v) => updateSettings({ mouseDpi: v })} />
      <div class="readout">
        <span class="field-label">Distance for a 360° turn</span>
        <span class="readout-value mono">{cm360.toFixed(1)} cm</span>
      </div>
      <Toggle label="Invert mouse" checked={s.invertMouse} onChange={(v) => updateSettings({ invertMouse: v })} />
      <Toggle label="Raw input" hint="Bypass OS mouse acceleration where the browser supports it. Applies the next time the mouse is captured." checked={s.rawInput} onChange={(v) => updateSettings({ rawInput: v })} />
    </>
  );
}

function VideoTab({ s }: { s: Settings }) {
  const app = useApp();
  return (
    <>
      <Slider label="Field of view" hint="Horizontal at 4:3, widened for wider screens (like cg_fov)." value={s.fov} min={60} max={140} step={1} unit="°" onChange={(v) => updateSettings({ fov: v })} />
      <Slider label="Brightness" value={s.brightness} min={0.5} max={2.5} step={0.05} onChange={(v) => updateSettings({ brightness: v })} />
      <Slider label="Render scale" hint={`Resolution of the 3D view: ${pct(s.renderScale)} of the screen's pixel density.`} value={s.renderScale} min={0.5} max={2} step={0.05} unit="×" onChange={(v) => updateSettings({ renderScale: v })} />
      <Toggle
        label="Low graphics"
        hint="For slower computers: rendering at 80 % without high-DPI, no anisotropic filtering, one cloud layer in the sky; antialiasing goes off after a reload. Applies fully to the next map loaded."
        checked={s.lowGraphics}
        onChange={(v) => updateSettings({ lowGraphics: v })}
      />
      <Toggle label="Show FPS" checked={s.showFps} onChange={(v) => updateSettings({ showFps: v })} />
      <p class="muted small">
        GPU: <span class="mono">{app.gpu.renderer}</span>
        {app.gpu.weakReasons.length > 0 && ` — looks slow (${app.gpu.weakReasons.join(', ')})`}
      </p>
    </>
  );
}

function SoundTab({ s }: { s: Settings }) {
  return (
    <>
      <Slider label="Master volume" value={Math.round(s.volume * 100)} min={0} max={100} step={1} unit="%" onChange={(v) => updateSettings({ volume: v / 100 })} />
      <Slider label="Effects" hint="Footsteps, landings, water, jump pads." value={Math.round(s.effectsVolume * 100)} min={0} max={100} step={1} unit="%" onChange={(v) => updateSettings({ effectsVolume: v / 100 })} />
      <Slider label="Ambience" hint="The map's speakers, doors and platforms." value={Math.round(s.ambientVolume * 100)} min={0} max={100} step={1} unit="%" onChange={(v) => updateSettings({ ambientVolume: v / 100 })} />
      <Toggle label="Other players' sounds" hint="Footsteps and landings of the other players in the room." checked={s.otherPlayerSounds} onChange={(v) => updateSettings({ otherPlayerSounds: v })} />
      <p class="muted small">Sounds come from the server's ET data. They start after the first click in the game (browsers block audio until then) and pause with the game.</p>
    </>
  );
}

/** The real HUD, rendered on a 1280x720 stage over a map image. */
function HudPreview({ s }: { s: Settings }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const hudRef = useRef<Hud | null>(null);
  const [scale, setScale] = useState(0.4);
  const map = s.recentMaps[0]?.name ?? 'tjtest';

  useEffect(() => {
    const hud = new Hud(hostRef.current!);
    hud.root.classList.add('hud-preview');
    hud.resize(1280, 720);
    hud.setContext({ online: true, noFallDamage: false });
    hud.showHelp(false);
    hud.chat('player', 'gg, nice run');
    hud.chat(null, 'jumper joined the map');
    hudRef.current = hud;
    const demo = () => {
      hud.split('CP 2', 8412);
      hud.message('Checkpoint reached', 'good', 2600);
    };
    demo();
    const t = setInterval(demo, 3000);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => wrapRef.current && setScale(wrapRef.current.clientWidth / 1280)) : null;
    if (wrapRef.current) {
      ro?.observe(wrapRef.current);
      setScale(wrapRef.current.clientWidth / 1280);
    }
    return () => {
      clearInterval(t);
      ro?.disconnect();
      hud.root.remove();
    };
  }, []);

  useEffect(() => {
    const hud = hudRef.current;
    if (!hud) return;
    hud.configure(s.hud, { showFps: s.showFps, largeText: s.largeHudText });
    hud.setHelpText(helpText(s.binds));
    // two frames so the speedometer trend shows
    for (const speed of [640, 652]) {
      hud.update({ speed, velocityZ: 180, health: 72, timer: { run: 'default', time: 12345 }, fps: 144, origin: [512, -96, 64], angles: [-4.5, 87.2, 0], noclip: false, keys: KEY_FORWARD | KEY_RIGHT | KEY_JUMP, extra: 'online: 3 players' });
    }
    hud.debugText = 'draw 169 calls · 14k tris · cluster 42';
    hud.drawCgaz(s.hud.cgaz ? { yaw: 30, velocity: [560, 330], move: { forwardmove: 127, rightmove: 127, upmove: 0 }, sprint: true, onGround: false, frametime: s.physicsMsec / 1000, hfov: s.fov } : null);
  }, [s.hud, s.showFps, s.largeHudText, s.binds, s.highContrast]);

  return (
    <div ref={wrapRef} class="hud-preview-wrap" aria-label="HUD preview" role="img">
      <div class="hud-preview-stage" style={{ transform: `scale(${scale})` }}>
        <Levelshot map={map} eager class="hud-preview-bg" />
        {/* the HUD adds its own DOM here */}
        <div ref={hostRef} class="hud-preview-host" />
      </div>
    </div>
  );
}

function HudTab({ s }: { s: Settings }) {
  const h = s.hud;
  return (
    <>
      <HudPreview s={s} />
      <div class="toggle-grid">
        <Toggle label="Timer" checked={h.timer} onChange={(v) => updateHud({ timer: v })} />
        <Toggle label="Splits" checked={h.splits} onChange={(v) => updateHud({ splits: v })} />
        <Toggle label="Speedometer" checked={h.speed} onChange={(v) => updateHud({ speed: v })} />
        <Toggle label="Keys" checked={h.keys} onChange={(v) => updateHud({ keys: v })} />
        <Toggle label="CGaz" checked={h.cgaz} onChange={(v) => updateHud({ cgaz: v })} />
        <Toggle label="Health" checked={h.health} onChange={(v) => updateHud({ health: v })} />
        <Toggle label="Chat" checked={h.chat} onChange={(v) => updateHud({ chat: v })} />
        <Toggle label="Debug info" checked={h.debug} onChange={(v) => updateHud({ debug: v })} />
      </div>
      <h3 class="section-title">ETJump training</h3>
      <div class="toggle-grid">
        <Toggle label="Overbounce detector" hint="J / F / B: a jump, a fall onto the floor under the crosshair, or below you overbounces." checked={h.obDetector} onChange={(v) => updateHud({ obDetector: v })} />
        <Toggle label="Sticky overbounces" hint="Also S: overbounces only with the 0.25 sticky offset (etj_drawOB 2)." checked={h.obSticky} onChange={(v) => updateHud({ obSticky: v })} />
        <Toggle label="Jump speeds" hint="Speed of each jump; green faster, red slower than the one before." checked={h.jumpSpeeds} onChange={(v) => updateHud({ jumpSpeeds: v })} />
        <Toggle label="Snaphud" hint="125 fps snapping zones of the wish direction." checked={h.snaphud} onChange={(v) => updateHud({ snaphud: v })} />
        <Toggle label="Upmove meter" hint="How long jump was held before and after landing." checked={h.upmoveMeter} onChange={(v) => updateHud({ upmoveMeter: v })} />
        <Toggle label="Strafe quality" hint="Share of frames that gained the most speed." checked={h.strafeQuality} onChange={(v) => updateHud({ strafeQuality: v })} />
        <Toggle label="Max speed" hint="Top speed since the last load, shown when you load." checked={h.maxSpeed} onChange={(v) => updateHud({ maxSpeed: v })} />
      </div>
      <Slider label="HUD scale" value={h.scale} min={0.75} max={2} step={0.05} unit="×" onChange={(v) => updateHud({ scale: v })} />
      <Slider label="Safe margin" hint="Distance of the HUD from the screen edges." value={h.safeMargin} min={0} max={10} step={0.5} unit="%" onChange={(v) => updateHud({ safeMargin: v })} />
      <Toggle label="Speed colour" hint="Green ▲ while gaining speed, red ▼ while losing it." checked={h.speedColor} onChange={(v) => updateHud({ speedColor: v })} />
      <Toggle label="Vertical speed" checked={h.verticalSpeed} onChange={(v) => updateHud({ verticalSpeed: v })} />
    </>
  );
}

function ControlsTab({ s }: { s: Settings }) {
  const app = useApp();
  const conflicts = findConflicts(s.binds);
  const conflicted = (code: string | null) => !!code && conflicts.has(code);
  const label = (a: Action) => ACTIONS.find((x) => x.action === a)?.label ?? a;
  const set = (action: Action, slot: 0 | 1, code: string | null) => {
    const { binds, displaced } = assign(s.binds, action, slot, code);
    updateSettings({ binds });
    if (displaced) toast(`${keyLabel(code)} was bound to ${label(displaced.action)} — moved its key there instead`, 'info');
  };
  let group = '';
  return (
    <>
      {conflicts.size > 0 && (
        <p class="warn-text" role="alert">
          ! {conflicts.size} key{conflicts.size > 1 ? 's are' : ' is'} bound to more than one action.
        </p>
      )}
      <table class="binds-table">
        <thead>
          <tr>
            <th>Action</th>
            <th>Primary</th>
            <th>Secondary</th>
          </tr>
        </thead>
        <tbody>
          {ACTIONS.map(({ action, label: text, group: g }) => {
            const head = g !== group;
            group = g;
            return [
              head && (
                <tr key={`g-${g}`} class="binds-group">
                  <th colSpan={3}>{g}</th>
                </tr>
              ),
              <tr key={action}>
                <td>{text}</td>
                {([0, 1] as const).map((slot) => (
                  <td key={slot}>
                    <KeyBind
                      code={s.binds[action][slot]}
                      label={`${text} ${slot ? 'secondary' : 'primary'}`}
                      conflict={conflicted(s.binds[action][slot])}
                      capture={() => app.input.captureNext()}
                      cancel={() => app.input.cancelCapture()}
                      onChange={(code) => set(action, slot, code)}
                    />
                  </td>
                ))}
              </tr>,
            ];
          })}
          <tr class="binds-fixed">
            <td>Pause / menu</td>
            <td>
              <span class="keybind fixed">Esc</span>
            </td>
            <td class="muted small">fixed</td>
          </tr>
        </tbody>
      </table>
      <p class="muted small">Click a slot and press a key, mouse button or wheel. Esc cancels, Backspace clears the slot.</p>
    </>
  );
}

function AccessibilityTab({ s }: { s: Settings }) {
  return (
    <>
      <Slider label="UI scale" hint="Size of the menus." value={s.uiScale} min={0.75} max={2} step={0.05} unit="×" onChange={(v) => updateSettings({ uiScale: v })} />
      <Toggle label="Reduce motion" hint="Turns off menu animations (also follows the system setting)." checked={s.reduceMotion} onChange={(v) => updateSettings({ reduceMotion: v })} />
      <Toggle label="Larger HUD text" checked={s.largeHudText} onChange={(v) => updateSettings({ largeHudText: v })} />
      <Toggle label="High contrast" hint="Brighter accent, text and lines." checked={s.highContrast} onChange={(v) => updateSettings({ highContrast: v })} />
    </>
  );
}

export function SettingsScreen({ onBack }: { onBack?: () => void }) {
  const [current, setCurrent] = useState<SettingsTab>('game');
  const s = settings.value;
  const reset = async () => {
    const name = TABS.find((t) => t.id === current)!.label;
    if (!(await confirmDialog({ title: `Reset ${name} settings?`, body: `All ${name.toLowerCase()} settings go back to their defaults.`, confirm: 'Reset', danger: true }))) return;
    const patch: Partial<Settings> = {};
    for (const key of TAB_KEYS[current]) {
      if (key === 'hud') patch.hud = { ...DEFAULT_HUD };
      else if (key === 'binds') patch.binds = cloneBinds(DEFAULT_BINDS);
      else (patch as Record<string, unknown>)[key] = DEFAULT_SETTINGS[key];
    }
    // the strafe helper switch lives in the HUD settings but shows on the Game tab
    if (current === 'game') patch.hud = { ...s.hud, cgaz: DEFAULT_HUD.cgaz };
    updateSettings(patch);
  };
  return (
    <div class="screen settings-screen">
      <div class="settings-side">
        {onBack && (
          <Button variant="ghost" icon="back" hint="ESC" onClick={onBack} class="settings-back">
            Back
          </Button>
        )}
        <Tabs items={TABS} value={current} onChange={setCurrent} orientation="vertical" label="Settings sections" />
      </div>
      <section class="panel settings-form" aria-label={`${TABS.find((t) => t.id === current)!.label} settings`}>
        <header class="panel-head">
          <h2 class="panel-title">{TABS.find((t) => t.id === current)!.label}</h2>
          <Button variant="ghost" size="sm" icon="restart" onClick={reset}>
            {current === 'controls' ? 'Reset all' : 'Reset to defaults'}
          </Button>
        </header>
        <div class="form-body">
          {current === 'game' && <GameTab s={s} />}
          {current === 'mouse' && <MouseTab s={s} />}
          {current === 'video' && <VideoTab s={s} />}
          {current === 'sound' && <SoundTab s={s} />}
          {current === 'hud' && <HudTab s={s} />}
          {current === 'controls' && <ControlsTab s={s} />}
          {current === 'accessibility' && <AccessibilityTab s={s} />}
        </div>
      </section>
    </div>
  );
}
